import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  BULK_ORDER_GRID_AXES,
  formatPowerLabel,
  toCartPowers,
  toQuantityKey,
} from '../../common/bulkOrder'
import { GridScrollArea } from './BulkOrderGridPanel.styles'
import BulkOrderGridCell from './BulkOrderGridCell'
import {
  GridTable,
  CornerHeader,
  CornerHeaderInner,
  CornerAxisLabel,
  CylHeader,
  SphHeader,
} from './BulkOrderSphCylGrid.styles'

// After a recap click that also changes diameter/side, wait for the grid DOM to rebuild
// before focusing the target cell (avoids focusing a stale/missing input).
const CONTEXT_CHANGE_FOCUS_DELAY_MS = 200

const BulkOrderSphCylGrid = ({
  grid = null,
  gridAxis = BULK_ORDER_GRID_AXES.CYLINDER,
  quantities = {},
  // Set by BulkOrderGridPanel when a recap line is clicked → scroll/focus that cell.
  pendingFocus = null,
  isCartEditable = true,
  onQuantityChange = () => {},
  // Clears pendingFocus in the parent; token ensures only this request is cleared.
  onFocusComplete = () => {},
}) => {
  const [focusedCell, setFocusedCell] = useState(null)
  const [showHeaderScrollFade, setShowHeaderScrollFade] = useState(false)
  const [headerRowHeight, setHeaderRowHeight] = useState(null)
  const tableRef = useRef(null)
  const focusTimerRef = useRef(null)

  const isAdditionGrid = gridAxis === BULK_ORDER_GRID_AXES.ADDITION
  const columns = isAdditionGrid ? grid?.additions : grid?.cylinders
  const columnAxisLabel = isAdditionGrid ? 'Add' : 'Cyl'

  const handleScrollFadeChange = useCallback(fade => {
    setShowHeaderScrollFade(current => current === fade.top ? current : fade.top)
  }, [])

  const handleFocusCell = useCallback((sphere, columnValue) => {
    setFocusedCell({ sphere, columnValue })
  }, [])

  const handleTableBlur = useCallback(event => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setFocusedCell(null)
    }
  }, [])

  const handleCellQuantityChange = useCallback(
    (sphere, columnValue, nextQuantity) => {
      if (!isCartEditable) return

      onQuantityChange({
        ...toCartPowers(sphere, columnValue, gridAxis),
        quantity: nextQuantity,
      })
    },
    [gridAxis, isCartEditable, onQuantityChange],
  )

  useEffect(() => {
    setShowHeaderScrollFade(false)
  }, [grid])

  useEffect(
    () => () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current)
    },
    [],
  )

  useEffect(() => {
    const table = tableRef.current
    const headerRow = table?.querySelector('thead tr')
    if (!headerRow) return undefined

    const updateHeaderRowHeight = () => {
      setHeaderRowHeight(`${headerRow.getBoundingClientRect().height}px`)
    }

    updateHeaderRowHeight()

    const resizeObserver = new ResizeObserver(updateHeaderRowHeight)
    resizeObserver.observe(headerRow)

    return () => {
      resizeObserver.disconnect()
    }
  }, [grid])

  // Honor pendingFocus from a product-recap line click: find the cell, scroll it into
  // view, focus its qty input, then ack the parent so pendingFocus can clear.
  useEffect(() => {
    if (!pendingFocus) return undefined

    // Nothing to focus (empty grid) — still ack so the parent does not keep a stale request.
    if (!grid?.spheres?.length || !columns?.length) {
      onFocusComplete(pendingFocus.token)
      return undefined
    }

    const { sphere, columnValue, token, hasContextChange } = pendingFocus
    const quantityKey = toQuantityKey(sphere, columnValue, gridAxis)
    const focusDelay = hasContextChange ? CONTEXT_CHANGE_FOCUS_DELAY_MS : 0

    focusTimerRef.current = setTimeout(() => {
      const cell = tableRef.current?.querySelector(`[data-quantity-key="${quantityKey}"]`)
      const input = cell?.querySelector('input')

      // Cell missing or disabled (e.g. not editable) — drop the request without focusing.
      if (!input || input.disabled) {
        onFocusComplete(token)
        return
      }

      cell.scrollIntoView({ block: 'nearest', inline: 'nearest' })
      input.focus({ preventScroll: true })
      setFocusedCell({ sphere, columnValue })
      onFocusComplete(token)
    }, focusDelay)

    // Cancel if pendingFocus changes, grid remounts, or the component unmounts mid-delay.
    return () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current)
    }
  }, [grid, columns, gridAxis, pendingFocus, onFocusComplete])

  if (!grid?.spheres?.length || !columns?.length) {
    return null
  }

  const { spheres, available } = grid

  return (
    <GridScrollArea
      horizontal
      onScrollFadeChange={handleScrollFadeChange}
    >
      <GridTable
        ref={tableRef}
        onBlur={handleTableBlur}
        style={headerRowHeight ? { '--grid-header-height': headerRowHeight } : undefined}
      >
        <thead>
          <tr>
            <CornerHeader $showScrollFade={showHeaderScrollFade}>
              <CornerHeaderInner>
                <CornerAxisLabel $axis="cyl">{columnAxisLabel}</CornerAxisLabel>
                <CornerAxisLabel $axis="sph">Sph</CornerAxisLabel>
              </CornerHeaderInner>
            </CornerHeader>
            {columns.map(columnValue => (
              <CylHeader
                key={columnValue}
                $isHighlighted={focusedCell?.columnValue === columnValue}
                $showScrollFade={showHeaderScrollFade}
              >
                {formatPowerLabel(columnValue)}
              </CylHeader>
            ))}
          </tr>
        </thead>
        <tbody>
          {spheres.map((sphere, rowIndex) => (
            <tr key={sphere}>
              <SphHeader $isHighlighted={focusedCell?.sphere === sphere}>
                {formatPowerLabel(sphere)}
              </SphHeader>
              {columns.map((columnValue, colIndex) => {
                const isAvailable = Boolean(available?.[rowIndex]?.[colIndex])
                const quantityKey = toQuantityKey(sphere, columnValue, gridAxis)

                return (
                  <BulkOrderGridCell
                    key={quantityKey}
                    quantityKey={quantityKey}
                    sphere={sphere}
                    columnValue={columnValue}
                    quantity={quantities[quantityKey] || 0}
                    isAvailable={isAvailable}
                    isCartEditable={isCartEditable}
                    onFocusCell={handleFocusCell}
                    onQuantityChange={handleCellQuantityChange}
                  />
                )
              })}
            </tr>
          ))}
        </tbody>
      </GridTable>
    </GridScrollArea>
  )
}

BulkOrderSphCylGrid.propTypes = {
  grid: PropTypes.shape({
    spheres: PropTypes.array,
    cylinders: PropTypes.array,
    additions: PropTypes.array,
    available: PropTypes.array,
  }),
  gridAxis: PropTypes.oneOf(Object.values(BULK_ORDER_GRID_AXES)),
  quantities: PropTypes.object,
  pendingFocus: PropTypes.shape({
    sphere: PropTypes.number.isRequired,
    columnValue: PropTypes.number.isRequired,
    token: PropTypes.number.isRequired,
    hasContextChange: PropTypes.bool,
  }),
  isCartEditable: PropTypes.bool,
  onQuantityChange: PropTypes.func,
  onFocusComplete: PropTypes.func,
}

export default BulkOrderSphCylGrid
